import NodeToolbar from '@/components/custom/editor/NodeToolbarButton'
import { KEYS } from '@/components/custom/editor/stringKeys/StringKeys'
import { getCurrentNode } from '@/features/screenplay/components/editor/utils/NodeUtils'
import { Position } from '@/features/screenplay/components/type'
import { useStoryStore } from '@/features/shared/stores/story.store'
import { computePosition, flip, offset, Placement } from '@floating-ui/dom'
import { LexicalEditor, NodeKey } from 'lexical'
import { Edit2, LucideIcon, Plus, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AIChatMode, useStAIWriterChat } from '../../StAIWriterChatProvider'
import { ParaNode } from '../nodes/ParaNode'

type InlineToolbarProps = {
    label: string
    icon: LucideIcon
    onClick: (editor: LexicalEditor) => void
    iconColor?: string
}

const StNodeToolbarPlugin = () => {
    const { openDrawer } = useStAIWriterChat()
    const xPosRef = useRef<number>(0)
    const ref = useRef<HTMLDivElement>(null)
    const [coords, setCoords] = useState<Position | undefined>(undefined)
    const { editor } = useStoryStore()

    const toolbarActions: InlineToolbarProps[] = [
        {
            label: 'Delete',
            icon: Trash2,
            onClick: (editor) => {
                editor.update(() => {
                    const currentNode = getCurrentNode()
                    if (currentNode) {
                        currentNode.remove()
                    }
                })
            },
            iconColor: 'text-red-500',
        },
        {
            label: 'AI',
            icon: Plus,
            onClick: () => openDrawer(AIChatMode.create_story),
        },
        {
            label: 'AI Edit',
            icon: Edit2,
            onClick: () =>
                openDrawer(
                    AIChatMode.edit_story,
                    editor?.read(() => {
                        const node = getCurrentNode()
                        if (node?.getType() === 'para') {
                            return [(node as ParaNode).exportContentJSON()]
                        }
                        return []
                    })
                ),
        },
    ]

    useEffect(() => {
        const updateXPos = async () => {
            const domElem = document.getElementById(KEYS.ST_CONTENT_EDITABLE)
            const floatingElem = ref.current
            if (domElem && floatingElem) {
                const result = await computePosition(domElem, floatingElem, {
                    middleware: [offset(10), flip()],
                    placement: 'right' as Placement,
                })
                xPosRef.current = result.x
            }
        }
        updateXPos()
        window.addEventListener('resize', updateXPos)
        return () => window.removeEventListener('resize', updateXPos)
    })

    const calculatePosition = useCallback(
        (key: NodeKey) => {
            const doPositioning = async () => {
                const _domElem = editor?.getElementByKey(key)
                const floatingElem = ref.current
                let coords: Position | undefined = undefined
                if (floatingElem && _domElem) {
                    try {
                        const YPos = await computePosition(_domElem, floatingElem, {
                            middleware: [offset(20), flip()],
                            placement: 'right-end' as Placement,
                        })
                        coords = { left: xPosRef.current, top: YPos.y }
                    } catch (error) {
                        console.error('Failed to compute position:', error)
                    }
                    setCoords(coords)
                }
            }
            setTimeout(doPositioning, 0)
        },
        [editor]
    )

    useEffect(() => {
        if (!editor) return
        const unregisterFns = editor?.registerUpdateListener(({ editorState }) => {
            editorState.read(() => {
                const currentNode = getCurrentNode()
                if (currentNode) {
                    calculatePosition(currentNode.__key)
                } else {
                    setCoords(undefined)
                }
            })
        })
        return () => {
            unregisterFns()
        }
    }, [editor])

    const dom = document.getElementById(KEYS.ST_EDITOR_CONTAINER)
    if (!editor) return
    if (!dom) return null
    return createPortal(
        <NodeToolbar coords={coords} toolbarActions={toolbarActions} editor={editor} ref={ref} />,
        dom
    )
}

export default StNodeToolbarPlugin
